import { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Icon } from "src/components/atoms/Icon/Icon";
import { Text } from "src/components/atoms/Text/Text";
import { FilterOption } from "src/utils/searchFilters";
import Styles from "./FilterPill.module.scss";

interface FilterPillProps {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Allow more than one value to be selected at a time. */
  multiple?: boolean;
}

export const FilterPill = ({ label, options, selected, onChange, multiple = false }: FilterPillProps) => {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close the menu when clicking anywhere outside the pill.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const active = selected.length > 0;

  const displayLabel = (() => {
    if (!active) return label;
    if (!multiple) {
      return options.find((option) => option.value === selected[0])?.label ?? label;
    }
    return `${label} (${selected.length})`;
  })();

  const toggleMenu = () => {
    // Anchor the menu to the right edge of the pill when it sits in the right
    // half of the screen, so it never spills off-screen on mobile.
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setAlignRight(rect.left > window.innerWidth / 2);
    }
    setOpen((value) => !value);
  };

  const handleSelect = (value: string) => {
    if (multiple) {
      onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
    } else {
      onChange(selected.includes(value) ? [] : [value]);
      setOpen(false);
    }
  };

  return (
    <div className={Styles.wrapper} ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        className={cn(Styles.pill, { [Styles.active]: active })}
        onClick={toggleMenu}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Text size="small" weight="semibold" color="gc-blue" noWrap>
          {displayLabel}
        </Text>
        <span className={cn(Styles.chevron, { [Styles.chevronOpen]: open })}>
          <Icon type="chevron" size="xsmall" fill="gcBlue" />
        </span>
      </button>

      {open && (
        <div className={cn(Styles.menu, { [Styles.menuRight]: alignRight })} role="listbox">
          {multiple && (
            <div className={Styles.clearRow}>
              <button
                type="button"
                className={cn(Styles.clearLink, { [Styles.clearDisabled]: !active })}
                onClick={() => active && onChange([])}
              >
                Clear
              </button>
            </div>
          )}
          {options.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={Styles.option}
                onClick={() => handleSelect(option.value)}
              >
                {option.icon && (
                  <span className={Styles.optionIcon} aria-hidden="true">
                    {option.icon}
                  </span>
                )}
                <Text
                  className={Styles.optionLabel}
                  color={isSelected && !multiple ? "gc-blue" : "off-black"}
                  weight={isSelected && !multiple ? "semibold" : "regular"}
                >
                  {option.label}
                </Text>
                {multiple ? (
                  <span className={cn(Styles.checkbox, { [Styles.checkboxChecked]: isSelected })} aria-hidden="true">
                    {isSelected && (
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12.5l5 5L20 6.5" />
                      </svg>
                    )}
                  </span>
                ) : (
                  isSelected && <Icon type="checkmark" size="xsmall" fill="gcBlue" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
